import { analyticsPage } from './privacy-consent.js';
import { plausibleOptedOut } from './plausible-privacy.js';
import { ANALYTICS_EVENTS } from './analytics-events.js';

// CE v3.2.1 /api/event protocol, not the Cloud-only revenue API/features.
// No remote tracker, automatic form/link capture, cookies, identifiers or replay queue.
export function createPlausibleTracker(
  target,
  {
    endpoint = 'https://meta.afjon.com/api/event',
    domain = 'idmanify.com',
    allowLocalhost = false
  } = {}
) {
  const active = new Set();
  let lastPage = '';
  let valid = false;
  try {
    const url = new URL(endpoint);
    valid =
      url.protocol === 'https:' &&
      !url.username &&
      !url.password &&
      url.pathname === '/api/event' &&
      !url.search &&
      !url.hash &&
      /^[a-z0-9.-]+$/.test(domain) &&
      domain.length <= 253;
  } catch {
    /* Invalid configuration disables optional tracking. */
  }
  const permitted = () => {
    if (!valid || plausibleOptedOut(target)) return false;
    let ignored = false;
    try {
      ignored = target.localStorage?.getItem('plausible_ignore') === 'true';
    } catch {
      /* Storage is optional. */
    }
    try {
      const host = target.location.hostname;
      return (
        (allowLocalhost || host === domain || host === `www.${domain}`) &&
        !ignored &&
        target.navigator?.globalPrivacyControl !== true &&
        target.navigator?.doNotTrack !== '1'
      );
    } catch {
      return false;
    }
  };
  function send(payload) {
    if (
      !permitted() ||
      !payload ||
      (payload.event !== 'page_view' &&
        !Object.hasOwn(ANALYTICS_EVENTS, payload.event))
    )
      return false;
    // Do not send transaction IDs, item IDs, account methods or financial/health data to CE.
    const page = analyticsPage(
      target.location.pathname,
      target.location.origin
    );
    const body = {
      n: payload.event === 'page_view' ? 'pageview' : payload.event,
      d: domain,
      u: page.page_location,
      r: '',
      ...(payload.event === 'view_item' ? { i: false } : {})
    };
    const controller = new AbortController();
    // Bound outstanding requests and their lifetime, including offline tabs.
    if (active.size >= 32) {
      const oldest = active.values().next().value;
      oldest.abort();
      active.delete(oldest);
    }
    active.add(controller);
    const timeout = setTimeout(() => {
      controller.abort();
      active.delete(controller);
    }, 10000);
    timeout.unref?.();
    try {
      const pending = target.fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain' },
        credentials: 'omit',
        referrerPolicy: 'no-referrer',
        keepalive: true,
        signal: controller.signal,
        body: JSON.stringify(body)
      });
      Promise.resolve(pending)
        .catch(() => {})
        .finally(() => {
          clearTimeout(timeout);
          active.delete(controller);
        });
      return true; // Accepted locally, not proof of server ingestion.
    } catch {
      clearTimeout(timeout);
      active.delete(controller);
      return false;
    }
  }
  function page() {
    if (!permitted()) return false;
    const key = analyticsPage(
      target.location.pathname,
      target.location.origin
    ).page_location;
    if (key === lastPage) return false;
    if (!send({ event: 'page_view' })) return false;
    lastPage = key;
    return true;
  }
  function revoke() {
    for (const controller of active) controller.abort();
    active.clear();
    lastPage = '';
  }
  return { send, page, revoke, allowed: permitted };
}
